import React from 'react';
import ReactDOM from 'react-dom/client';
import { GlobalErrorBoundary, installChunkReloadHandler } from '@pc/ui';
import { initAnalytics } from '@pc/analytics-client';
import { getSession } from '@pc/db';
import { initI18n } from '@pc/i18n';
import { App } from './App';
import { env } from './env';
import './index.css';

// Backstop for the stale-chunk failure mode every Vite SPA hits on deploy:
// a user with the old shell loaded clicks a nav link, React.lazy fires
// `import('/assets/ProductsPage-<oldhash>.js')`, Netlify 404s the purged
// chunk, and react-router renders "Unexpected Application Error! Failed
// to fetch dynamically imported module". `lazyWithReload` in App.tsx
// already covers every code-split route; this listener catches the OTHER
// dynamic imports (i18n bundles, third-party libs, ad-hoc `await
// import()` inside event handlers). MUST run before any React mount so
// the listener is attached when the first chunk lands.
installChunkReloadHandler();

// react-i18next bootstrap. Picks up the localStorage preference + browser
// Accept-Language for unauthenticated visitors; the language picker in
// ProfilePage flips this and persists to core.users.preferred_locale once
// the user is authenticated.
initI18n({ app: 'sell' });

// Self-hosted pageview analytics. trackPageview() no-ops until the
// SellShell mounts useRouteTracking() — this just primes the
// singleton with the sell app id + supabase config so the hook can
// fire on the first authenticated paint.
initAnalytics({
  app: 'sell',
  supabaseUrl: env.supabaseUrl,
  anonKey: env.supabaseAnonKey,
  getSession: async () => {
    const s = await getSession();
    return s ? { access_token: s.access_token } : null;
  },
});

const rootEl = document.getElementById('root');
if (!rootEl) throw new Error('Root element #root not found.');

ReactDOM.createRoot(rootEl).render(
  <React.StrictMode>
    <GlobalErrorBoundary>
      <App />
    </GlobalErrorBoundary>
  </React.StrictMode>,
);
